import React from 'react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const isPropsDrivenChart = (chart: ChartCatalogEntry): boolean =>
    Boolean(chart.props && Object.keys(chart.props).length > 0);

export interface ChartCatalogEntry {
    id: string;
    title: string;
    type: string;
    category?: string;
    component: ComponentType<any>;
    data: any;
    options?: any;
    /** Extra props spread onto the rendered component — lets the catalog drive
     *  presentational components (HeroCard, AnalyticsOverviewCard, HeatmapChart)
     *  with external showcase values instead of their internal defaults. */
    props?: Record<string, any>;
    /** Legacy illustrative snippet. No longer rendered — ChartDetail generates
     *  the component usage code live. Optional; safe to drop from entries. */
    code?: string;
    description?: string;
    reportCodegenDefaultHint?: string;
}

export interface ChartCardProps {
    chart: ChartCatalogEntry;
    onClick: (id: string) => void;
    databaseId?: string;
    reportNote?: string;
    onReportNoteChange?: (id: string, value: string) => void;
}

const ChartCard: React.FC<ChartCardProps> = ({
    chart,
    onClick,
    databaseId,
    reportNote = '',
    onReportNoteChange,
}) => {
    // Editors/admins may edit report notes; viewers see them read-only.
    const { canWrite } = useAuth();
    const { t } = useTranslation('charts');
    const ChartComp = chart.component as ComponentType<any>;
    const previewData = chart.data;
    const previewOptions = chart.options;
    const extraProps = chart.props || {};
    const propsDriven = isPropsDrivenChart(chart);

    const isSpecial = chart.type === 'DASHBOARD' || chart.type === 'KPI';
    const showNote = Boolean(databaseId && typeof onReportNoteChange === 'function');
    const isTable = String(chart.type || '').toUpperCase() === 'TABLE';
    // The standard chart components (line/bar/pie/doughnut/radar/polar/heatmap)
    // draw their own single border + title via ChartFrame, so the card passes
    // the title down and skips its own border/header for them. Tables and
    // dashboard cards keep the card chrome below.
    // Every chart AND table component draws its own ChartFrame (border + title +
    // type badge), so the card just passes the title/badge down. Only dashboards
    // (special) bring their own chrome.
    const ownsFrame = !isSpecial;
    const frameProps = ownsFrame ? { title: chart.title, badge: chart.type } : {};

    const chartPreview = propsDriven ? (
        <ChartComp {...extraProps} {...frameProps} />
    ) : (
        <ChartComp data={previewData} options={previewOptions} {...frameProps} />
    );

    const noteBlock = showNote && onReportNoteChange ? (
        <div
            className={
                isSpecial
                    ? 'mt-3 px-3 sm:px-4 pb-3 bg-white rounded-theme border border-gray-100 shadow-sm'
                    : 'border-t border-gray-100 px-4 sm:p-theme pt-3 pb-4 bg-gray-50/80'
            }
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                {canWrite ? t('card.reportNoteLabel') : t('card.reportNoteLabelReadOnly')}
            </label>
            <textarea
                value={reportNote}
                onChange={canWrite ? (e) => onReportNoteChange(chart.id, e.target.value) : undefined}
                readOnly={!canWrite}
                rows={2}
                placeholder={
                    canWrite
                        ? t('card.reportNotePlaceholder')
                        : t('card.reportNoteEmpty')
                }
                className={`w-full text-xs sm:text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none resize-y min-h-[52px] pointer-events-auto ${
                    canWrite
                        ? 'text-gray-800 bg-white focus:border-primary-300 focus:ring-2 focus:ring-primary-500/15'
                        : 'text-gray-500 bg-gray-100 cursor-default'
                }`}
            />
        </div>
    ) : null;

    if (isSpecial) {
        return (
            <div className="rounded-theme overflow-hidden">
                <div onClick={() => onClick(chart.id)} className="cursor-pointer rounded-theme overflow-hidden">
                    {chartPreview}
                </div>
                {noteBlock}
            </div>
        );
    }

    // The chart/table component owns its ChartFrame (border + title + type
    // badge); the card renders only a clickable wrapper so the single visible
    // border comes from the component. Charts get a fixed height; tables size to
    // their content (and scroll internally).
    return (
        <div className="group font-sans flex flex-col">
            <div
                onClick={() => onClick(chart.id)}
                className={`cursor-pointer shrink-0 transition-transform duration-300 hover:-translate-y-1 ${
                    isTable ? '' : 'h-[22rem]'
                }`}
            >
                {chartPreview}
            </div>
            {noteBlock}
        </div>
    );
};

export default ChartCard;
